/**
 * Global WebSocket context - maintains connection across page navigations
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import { GameState } from '../services/api';

// Dynamic WebSocket URL based on current hostname
const getWsUrl = (playerId: string) => {
  const { protocol, hostname } = window.location;
  const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:';

  // Production: ar-mandarin.app -> api.ar-mandarin.app
  if (hostname === 'ar-mandarin.app' || hostname === 'www.ar-mandarin.app') {
    return `${wsProtocol}//api.ar-mandarin.app/api/ws/${playerId}`;
  }

  // Development: localhost with port 8000
  return `${wsProtocol}//${hostname}:8000/api/ws/${playerId}`;
};

interface SocketContextType {
  isConnected: boolean;
  gameState: GameState | null;
  error: string | null;
  connect: () => void;
  disconnect: () => void;
  setPlayerId: (id: string | null) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Don't auto-connect on app load - only connect after explicit login
  const [playerId, setPlayerIdState] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intentionalCloseRef = useRef<boolean>(false);
  const reconnectAttemptsRef = useRef<number>(0);

  const connect = useCallback(() => {
    if (!playerId) return;

    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Don't reconnect if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    // Close existing connection without triggering reconnect
    if (wsRef.current) {
      intentionalCloseRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
    }

    const wsUrl = getWsUrl(playerId);
    console.log('Connecting to WebSocket:', wsUrl);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      intentionalCloseRef.current = false;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          if (message.type === 'state') {
            const state = message.data as GameState;
            setGameState(state);
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Only reconnect if not intentional close
        if (!intentionalCloseRef.current && playerId) {
          reconnectAttemptsRef.current += 1;
          // Exponential backoff: 1s, 2s, 4s, max 10s
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 10000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})...`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };

      ws.onerror = (event) => {
        console.error('WebSocket error:', event);
        setError('Connection error');
      };
    } catch (e) {
      console.error('Failed to create WebSocket:', e);
      setError('Failed to connect');
    }
  }, [playerId]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      intentionalCloseRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  const setPlayerId = useCallback((id: string | null) => {
    setPlayerIdState(id);
    if (id) {
      localStorage.setItem('playerId', id);
    } else {
      localStorage.removeItem('playerId');
    }
  }, []);

  // Connect when playerId changes
  useEffect(() => {
    if (playerId) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      // Only cleanup reconnect timeout, don't disconnect on unmount
      // because this is a global provider
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [playerId, connect, disconnect]);

  // Handle visibility change - reconnect when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && playerId) {
        console.log('Page became visible, checking connection...');
        // Check if connection is still alive
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          console.log('Connection lost, reconnecting...');
          connect();
        }
      }
    };

    // Handle online/offline events
    const handleOnline = () => {
      console.log('Network online, reconnecting...');
      if (playerId && (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) {
        connect();
      }
    };

    const handleOffline = () => {
      console.log('Network offline');
      setError('Network offline');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [playerId, connect]);

  // Heartbeat to detect stale connections
  useEffect(() => {
    if (!isConnected || !wsRef.current) return;

    const heartbeatInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        // Send a ping (server should respond with pong or state)
        // If WebSocket is actually dead, this will trigger onclose
        try {
          wsRef.current.send(JSON.stringify({ type: 'ping' }));
        } catch (e) {
          console.log('Heartbeat failed, reconnecting...');
          connect();
        }
      }
    }, 30000); // Every 30 seconds

    return () => clearInterval(heartbeatInterval);
  }, [isConnected, connect]);

  return (
    <SocketContext.Provider
      value={{
        isConnected,
        gameState,
        error,
        connect,
        disconnect,
        setPlayerId,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
